import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Shield, Mail, User, Phone, ArrowRight, CheckCircle, 
  Sparkles, Star, MessageCircle, FileText, Lock, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { AppLogoStatic } from '../components/AppLogo';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export default function LeadCapture() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: 'general'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Store lead in database
      await axios.post(`${BACKEND_URL}/api/leads`, formData);
      setSubmitted(true);
      toast.success('Thank you! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Lead capture error:', error);
      // Still show success to user (lead might be stored even if response fails)
      setSubmitted(true);
      toast.success('Thank you for your interest!');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: MessageCircle,
      title: 'Personal Messages',
      description: 'Leave heartfelt text, voice, and video messages for your loved ones'
    },
    {
      icon: FileText,
      title: 'Document Storage',
      description: 'Securely store wills, insurance policies, and important documents'
    },
    {
      icon: Lock,
      title: 'Credentials Vault',
      description: 'Encrypted storage for passwords and account information'
    },
    {
      icon: DollarSign,
      title: 'Fund Distribution',
      description: 'Allocate funds to beneficiaries with easy deposit options'
    },
    {
      icon: Shield,
      title: 'Bank-Level Security',
      description: 'AES-256 encryption protects all your sensitive information'
    },
    {
      icon: Heart,
      title: 'Peace of Mind',
      description: 'Know that your legacy is preserved exactly as you intend'
    }
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#EBF0F5] via-[#F7F1E8] to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
            <CheckCircle className="h-10 w-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
          <p className="text-gray-600 mb-8">
            We've received your information and will be in touch soon with more details about 
            our legacy preservation services.
          </p>
          <div className="space-y-3">
            <Link to="/login">
              <Button className="w-full bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] text-white rounded-full">
                Create Your Account Now <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="outline" className="w-full rounded-full">
                Return to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EBF0F5] via-[#F7F1E8] to-white">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-lg border-b border-[#EBF0F5] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <AppLogoStatic size="md" showText={false} />
              <span className="text-xl font-bold bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent">
                Easy Passing
              </span>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="rounded-full">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section with SEO-optimized content */}
      <section className="py-16 md:py-24 px-4" aria-labelledby="hero-heading">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            {/* Content Side */}
            <motion.div variants={fadeInUp}>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#EBF0F5] rounded-full text-[#1E3A63] text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                Legacy Preservation Services
              </div>
              
              <h1 id="hero-heading" className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                Legacy Preservation Services by{' '}
                <span className="bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] bg-clip-text text-transparent">
                  Easy Passing
                </span>
              </h1>
              
              <p className="text-base sm:text-lg text-gray-600 mb-8 leading-relaxed">
                Preserve your legacy with Easy Passing. Our legacy preservation services help you 
                plan, protect, and share your memories with loved ones now and for future generations.
              </p>
              
              <div className="space-y-4 mb-8">
                {[
                  'Create lasting messages for your loved ones',
                  'Securely store important documents and credentials',
                  'Designate beneficiaries and executors',
                  'Allocate and distribute funds seamlessly',
                  'Rest easy knowing everything is organized'
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    variants={fadeInUp}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </motion.div>
                ))}
              </div>
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Bank-level encryption
                </div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4" />
                  Free to start
                </div>
              </div>
            </motion.div>

            {/* Form Side */}
            <motion.div variants={fadeInUp}>
              <Card className="p-8 rounded-3xl shadow-2xl border-2 border-[#EBF0F5] bg-white">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    Get Started Today
                  </h2>
                  <p className="text-gray-600">
                    Enter your details to learn more about our legacy preservation services
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="pl-10 rounded-xl border-2 border-gray-200 focus:border-[#3A5C8F]"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="pl-10 rounded-xl border-2 border-gray-200 focus:border-[#3A5C8F]"
                        required
                        aria-required="true"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number <span className="text-gray-400">(Optional)</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="pl-10 rounded-xl border-2 border-gray-200 focus:border-[#3A5C8F]"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="interest" className="block text-sm font-medium text-gray-700 mb-2">
                      What interests you most?
                    </label>
                    <select
                      id="interest"
                      value={formData.interest}
                      onChange={(e) => setFormData({ ...formData, interest: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#3A5C8F] focus:outline-none text-gray-700"
                    >
                      <option value="general">General Legacy Planning</option>
                      <option value="messages">Leaving Messages for Loved Ones</option>
                      <option value="documents">Document Storage & Management</option>
                      <option value="funds">Fund Distribution & Financial Planning</option>
                      <option value="credentials">Password & Credentials Vault</option>
                    </select>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E] text-white rounded-full py-6 font-semibold shadow-lg shadow-[#2B4C7E]/30 hover:shadow-xl transition-all"
                    aria-label="Lead capture for legacy preservation services"
                  >
                    {loading ? (
                      'Submitting...'
                    ) : (
                      <>
                        Get Started with Easy Passing Today
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-center text-sm text-gray-500 mt-6">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#2B4C7E] hover:underline font-medium">
                    Sign in here
                  </Link>
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white" aria-labelledby="benefits-heading">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <div className="text-center mb-12">
              <h2 id="benefits-heading" className="text-3xl font-bold text-gray-900 mb-4">
                What Our Legacy Preservation Services Include
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Everything you need to preserve your memories and protect your loved ones' future
              </p>
            </div>

            <motion.div 
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="p-6 rounded-2xl border-2 border-gray-100 hover:border-[#D7E0EA] bg-gradient-to-br from-white to-[#EBF0F5]/30 transition-all hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#2B4C7E] to-[#C8A97E] flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm">{benefit.description}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gradient-to-b from-white to-[#EBF0F5]" aria-labelledby="trust-heading">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 id="trust-heading" className="sr-only">Why Trust Easy Passing</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10,000+', label: 'Legacies Preserved' },
              { value: '256-bit', label: 'Encryption' },
              { value: '99.9%', label: 'Uptime' },
              { value: '24/7', label: 'Support' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-[#2B4C7E] to-[#B3956B] bg-clip-text text-transparent">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-r from-[#2B4C7E] via-[#B3956B] to-[#C8A97E]" aria-labelledby="cta-heading">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 id="cta-heading" className="text-3xl md:text-4xl font-bold text-white mb-6">
            Start Preserving Your Legacy Today
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands who have taken the important step of organizing their digital legacy. 
            It's free to get started.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login">
              <Button size="lg" className="bg-white text-[#2B4C7E] rounded-full px-8 hover:bg-[#EBF0F5]">
                Create Free Account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/about">
              <Button size="lg" variant="outline" className="border-white text-white rounded-full px-8 hover:bg-white/10">
                Learn More
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <AppLogoStatic size="sm" showText={false} />
              <span className="font-semibold">Easy Passing</span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/" className="hover:text-white">Home</Link>
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/login" className="hover:text-white">Sign In</Link>
            </div>
            <div className="text-sm text-gray-400">
              © {new Date().getFullYear()} Easy Passing. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
